import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { getSelectedStudioId } from "../hooks/useAuthentication";
import { routePaths } from "../routes/routePaths";
import { buildRequestHeaders } from "./axiosHeaders";

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || "https://localhost:7279/api",
  timeout: 2 * 60 * 1000,
});

let axiosInterceptorId: number | null = null; // Track the interceptor ID
let invalidDomainDetected = false; // Flag to prevent multiple redirects

// Response interceptor to handle invalid domain errors from backend
axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // Check if backend returned 451 status code (invalid domain)
    // 451 Unavailable For Legal Reasons - semantically appropriate for domain restrictions
    if (
      !invalidDomainDetected &&
      error.response?.status === 451 &&
      window.location.pathname !== routePaths.invalidDomain
    ) {
      invalidDomainDetected = true;
      window.location.href = routePaths.invalidDomain;
    }
    return Promise.reject(error);
  }
);

// Attach token, studio ID, etc. before each request
export const setAxiosToken = (token?: string) => {
  if (axiosInterceptorId !== null) {
    axiosInstance.interceptors.request.eject(axiosInterceptorId);
  }

  axiosInterceptorId = axiosInstance.interceptors.request.use(
    (config: AxiosRequestConfig) => {
      const selectedStudioId = getSelectedStudioId();
      config.headers = buildRequestHeaders(config.headers, selectedStudioId);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
};

export function isAxiosError(error: unknown): error is AxiosError {
  return axios.isAxiosError && axios.isAxiosError(error);
}

export default axiosInstance;
